import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { transparentize } from 'polished';
import * as React from 'react';
import { ReactNode } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { strIsNullOrEmpty } from '@fox/shared';
import { Icon } from '../icon/index';
import { ListItemIcon, ListItemText, MuiMenuItem } from '../mui-list/index';
import { Popover, Position } from '../popover/index';
import { ConfirmPopover } from '../popover/popovers';

export const Menu2Wrap = styled.div`
	padding: 5px;
	outline: none;
	overflow: hidden;
`;

export const Item = styled.div`
	display: flex;
	cursor: pointer;
	transition: all 0.2s ease-in-out;
	min-height: 40px;
	padding-right: 5px;
	font-size: 0.95rem;
	font-weight: 300;
	outline: none;
`;

export const ItemIcon = styled.div`
	display: flex;
	flex-direction: columns;
	justify-content: space-around;
	//z-index: 999999;
	outline: none;
`;

export const ItemText = styled.div`
	color: ${props => props.theme.palette.swiftfox.neutral['60-darkGrey']};
	margin-top: auto;
	//z-index: 999999;
	margin-bottom: auto;
	width: 100%;
	overflow: hidden;
`;

export const CollapseIconWrapper = styled.div`
	margin: auto;
	display: flex;
	height: fit-content;
	/* padding-left: 10px; */
	cursor: pointer;
`;

export interface IMenu2Option {
	value?: string;
	selectable?: boolean;
}

interface IMenu2OptionProps extends IMenu2Option {
	icon?: React.ReactElement;
	label: React.ReactNode;
	children?: React.ReactElement<IMenu2Option> | React.ReactElement<IMenu2Option>[];
	// data?: ({ label?: React.ReactNode } & IMenu2Option)[];
	onClick?: () => void;

	expanded?: boolean;
	noWrap?: boolean;
	menu?: React.ReactElement;
	focused?: boolean;
}

const Menu2OptionContext = React.createContext({
	collapse: () => undefined
});

// Hook
function useKeyPress(targetKey: any) {
	// State for keeping track of whether key is pressed
	const [keyPressed, setKeyPressed] = React.useState(false);

	// If pressed key is our target key then set to true
	function downHandler({ key }: any) {
		if (key === targetKey) {
			setKeyPressed(true);
		}
	}

	// If released key is our target key then set to false
	const upHandler = ({ key }: any) => {
		if (key === targetKey) {
			setKeyPressed(false);
		}
	};

	// Add event listeners
	React.useEffect(() => {
		window.addEventListener('keydown', downHandler);
		window.addEventListener('keyup', upHandler);
		// Remove event listeners on cleanup
		return () => {
			window.removeEventListener('keydown', downHandler);
			window.removeEventListener('keyup', upHandler);
		};
	}, []); // Empty array ensures that effect is only run on mount and unmount

	return keyPressed;
}

export const CollapseIcon = (props: { isExpanded: boolean; handleClick: () => void }) => {
	const { isExpanded, handleClick } = props;

	return (
		<motion.div
			animate={{
				transform: `rotate(${!isExpanded ? -90 : 0}deg)`
			}}
			onClick={handleClick}
		>
			<Icon sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center' }} iconName={'ArrowDropDown'} />
		</motion.div>
	);
};

const Menu2Context = React.createContext({
	setFocusedMenuOption: (value: string, rect: ScreenPos) => undefined,
	focusedMenuValue: '',
	addChildOption: (obj: React.RefObject<HTMLDivElement>, value: string) => undefined,
	removeChildOption: (values: string) => undefined,
	onSelect: (value: string) => undefined,
	highlightedValues: []
});

export const safeKeyString = (label: ReactNode): string => {
	if (typeof label === 'string') return label;
	if (typeof label === 'number' || typeof label === 'boolean') return `${label}`;
	if (Array.isArray(label)) return safeKeyString(label[0]);

	// ReactElement has key
	if (typeof (label as any)?.key === 'string') return (label as any).key;

	return 'nokey';
};

export const Menu2Option = (props: IMenu2OptionProps) => {
	const i = props;
	const { noWrap = true } = props;
	const theme = React.useContext(ThemeContext);
	const [expanded, setExpanded] = React.useState(props.expanded);
	React.useEffect(() => {
		if (props.expanded !== undefined) {
			setExpanded(props.expanded);
		}
	}, [props.expanded]);

	const context = React.useContext(Menu2Context);
	const ref = React.useRef<HTMLDivElement>();

	const optionContext = React.useContext(Menu2OptionContext);

	const left = useKeyPress('ArrowLeft');
	const right = useKeyPress('ArrowRight');
	const enter = useKeyPress('Enter');

	const allowSelect =
		props.selectable !== undefined
			? props.selectable
			: props.children === undefined || (Array.isArray(props.children) && props.children?.length === 0);
	React.useEffect(() => {
		if (allowSelect && enter && context.focusedMenuValue === props.value) {
			context.onSelect(props.value);
		}

		if (enter && context.focusedMenuValue === props.value) {
			if (props.children) {
				setExpanded(true);
			}
		}
	}, [enter]);

	React.useEffect(() => {
		if (left && context.focusedMenuValue === props.value) {
			if (props.children) {
				setExpanded(false);
			} else {
				if (optionContext) optionContext.collapse();
			}
		}
	}, [left]);

	React.useEffect(() => {
		if (right && context.focusedMenuValue === props.value) {
			if (props.children) {
				setExpanded(true);
			}
		}
	}, [right]);

	React.useLayoutEffect(() => {
		context.addChildOption(ref, props.value);
		return () => context.removeChildOption(props.value);
	}, [ref]);

	const handleClick = () => {
		if (props.onClick) {
			props.onClick();
		}
		if (allowSelect) {
			context.onSelect(props.value);
		} else {
			setExpanded(!expanded);
		}
	};

	const handleIconClick = () => {
		setExpanded(!expanded);
	};

	return (
		<Menu2OptionContext.Provider
			value={{
				collapse: () => {
					context.setFocusedMenuOption(props.value, {} as any);
					if (props.children) setExpanded(false);
				}
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%'
				}}
			>
				{context.focusedMenuValue === props.value && (
					<motion.div
						transition={{
							type: 'linear',
							duration: 0.2
						}}
						style={{ pointerEvents: 'none', zIndex: 0, gridColumnStart: 1, gridRowStart: 1, width: '100%', height: '100%' }}
						layout="position"
						layoutId="menuFocus"
					>
						<ItemBackground />
					</motion.div>
				)}

				{props.menu ? (
					<div
						ref={ref}
						style={{
							display: 'grid',
							width: ' 100%',
							alignItems: 'center',
							gridTemplate: '1fr / 1fr 40px'
						}}
					>
						<MuiMenuItem
							key={`menuitem_${props.value ?? safeKeyString(props.label)}`}
							disableGutters={true}
							dense={true}
							style={{
								width: '100%',
								whiteSpace: noWrap ? 'nowrap' : 'normal',
								borderRadius: '4px'
							}}
							sx={{ '&:hover': { backgroundColor: theme.palette.swiftfox.neutral['20-offWhite'] } }}
							selected={context.highlightedValues.indexOf(props.value) >= 0}
							onClick={c => {
								handleClick();
							}}
						>
							<div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
								{Array.isArray(props.children) && props.children?.length > 0 ? (
									<div className="collapse-icon-wrapper" style={{ cursor: 'pointer', marginRight: '5px' }}>
										<CollapseIcon isExpanded={expanded} handleClick={handleIconClick} />
									</div>
								) : (
									<div style={{ width: '10px' }}></div>
								)}
								<ListItemText>{i.label}</ListItemText>
							</div>
						</MuiMenuItem>

						{props.menu}
					</div>
				) : (
					<div
						ref={ref}
						style={{ display: 'flex', width: '100%', gridColumnStart: 1, gridRowStart: 1 }}
						key={`menuctr_${props.value ?? safeKeyString(props.label)}`}
					>
						{Array.isArray(props.children) && props.children?.length > 0 && (
							<CollapseIconWrapper className="collapse-icon-wrapper" style={{ margin: noWrap ? 'auto' : '6px 0px' }}>
								<CollapseIcon isExpanded={expanded} handleClick={handleIconClick} />
							</CollapseIconWrapper>
						)}
						<MuiMenuItem
							key={`menuitem_${props.value ?? safeKeyString(props.label)}`}
							style={{
								width: '100%',
								whiteSpace: noWrap ? 'nowrap' : 'normal'
							}}
							selected={context.highlightedValues.indexOf(props.value) >= 0}
							onClick={c => {
								handleClick();
							}}
						>
							{i.icon && <ListItemIcon>{i.icon}</ListItemIcon>}
							<ListItemText>{i.label}</ListItemText>
						</MuiMenuItem>
					</div>
				)}
			</div>
			<AnimatePresence>
				{props.children && expanded && (
					<motion.div
						transition={{
							type: 'ease-in-out',
							duration: 0.2
						}}
						layout="position"
						exit={{ opacity: 0, height: 0, marginLeft: 0 }}
						initial={{ opacity: 0, height: 0, marginLeft: 0 }}
						animate={{ opacity: 1, height: 'auto', width: '100%', paddingLeft: '2rem' }}
					>
						<ItemCollection>{props.children}</ItemCollection>
					</motion.div>
				)}
			</AnimatePresence>
		</Menu2OptionContext.Provider>
	);
};
export interface ISelectOptionData extends IMenu2Option {
	label?: React.ReactNode;
	tagLabel?: React.ReactNode;
	icon?: React.ReactElement;
	children?: ISelectOptionData[];
	/**
	 * Arbitrary data to attach to the select option
	 */
	meta?: any;
}
export interface IMenu2Props {
	children?: React.ReactElement<IMenu2Option> | React.ReactElement<IMenu2Option>[];
	data?: ISelectOptionData[];
	style?: React.CSSProperties;
	highlightedValues?: string[];
	forcedFocusedIndex?: number;
	onSelect?: (value: string) => void;
}

const HighlightWrap = styled.div`
	display: grid;
	grid-template-columns: 1fr;
`;

const ItemBackground = styled(motion.div)`
	width: 100%;
	height: 100%;
	pointer-events: none;
	background-color: ${props => transparentize(0.8, props.theme.palette.primary.main)};
`;

interface ScreenPos {
	top: number;
	left: number;
	width: number;
	height: number;
}

export const ItemCollection = (props: IMenu2Props) => {
	const { children, data } = props;

	return (
		<div>
			{data?.map((d, i) => {
				return <Menu2Option icon={d.icon} key={`${d.value}${i}`} selectable={d.selectable} value={d.value} label={d.label} />;
			})}

			{React.Children.map(children, (i, index) => {
				return <div key={index}>{i}</div>;
			})}
		</div>
	);
};

const useMousePosition = () => {
	const [mousePosition, setMousePosition] = React.useState({ x: null, y: null });

	const updateMousePosition = (ev: any) => {
		setMousePosition({ x: ev.clientX, y: ev.clientY });
	};

	React.useEffect(() => {
		window.addEventListener('mousemove', updateMousePosition);

		return () => window.removeEventListener('mousemove', updateMousePosition);
	}, []);

	return mousePosition;
};

export const Menu2 = (props: IMenu2Props) => {
	const { children } = props;

	const menuRect = React.useRef<HTMLDivElement>();

	const [focusedIdx, setFocusedIdx] = React.useState(props.forcedFocusedIndex);
	React.useEffect(() => {
		setFocusedIdx(props.forcedFocusedIndex);
	}, [props.forcedFocusedIndex]);

	const allOptions = React.useRef<{
		[key: string]: React.RefObject<HTMLDivElement>;
	}>();
	const valueInOrder: { y: number; value: string }[] = allOptions?.current
		? Object.keys(allOptions.current)
				.map(o => {
					return {
						y: allOptions.current[o]?.current?.offsetTop,
						value: o
					};
				})
				.sort((a, b) => a.y - b.y)
		: [];

	const selectedVal = valueInOrder[focusedIdx]?.value;
	const [domRect, setDomRect] = React.useState<ScreenPos>(
		!strIsNullOrEmpty(selectedVal)
			? {
					left: allOptions.current[selectedVal]?.current?.offsetLeft,
					top: allOptions.current[selectedVal]?.current?.offsetTop,
					width: allOptions.current[selectedVal]?.current?.offsetWidth,
					height: allOptions.current[selectedVal]?.current?.offsetHeight
				}
			: {
					left: menuRect.current?.offsetLeft,
					top: menuRect.current?.offsetTop,
					width: menuRect.current?.offsetWidth,
					height: menuRect.current?.offsetHeight
				}
	);

	const setToMenuRect = () => {
		setDomRect({
			left: menuRect.current?.offsetLeft,
			top: menuRect.current?.offsetTop,
			width: menuRect.current?.offsetWidth,
			height: menuRect.current?.offsetHeight
		});
	};

	const updateDomRect = () => {
		setDomRect(
			!strIsNullOrEmpty(selectedVal)
				? {
						left: allOptions.current[selectedVal]?.current?.offsetLeft,
						top: allOptions.current[selectedVal]?.current?.offsetTop,
						width: allOptions.current[selectedVal]?.current?.offsetWidth,
						height: allOptions.current[selectedVal]?.current?.offsetHeight
					}
				: {
						left: menuRect.current?.offsetLeft,
						top: menuRect.current?.offsetTop,
						width: menuRect.current?.offsetWidth,
						height: menuRect.current?.offsetHeight
					}
		);
	};

	/*React.useEffect(() => {
		const action = () => {
			updateDomRect();
			return setTimeout(() => updateDomRect(), 500);// window.requestAnimationFrame(action)
		}
		const res = action();

		return () => clearTimeout(res);
	}, [])*/

	React.useEffect(() => {
		updateDomRect();
	}, [selectedVal]);

	const showHighlight = selectedVal !== undefined;

	const up = useKeyPress('ArrowUp');
	const down = useKeyPress('ArrowDown');

	const [focusedIdxIsMouseOver, setFocusedIdxIsMouseOver] = React.useState(true);

	// capture the time of the last arrow scroll to block the mouse cursor from overriding highlight as a result of the scroll
	const lastArrowScroll = React.useRef(null);

	React.useEffect(() => {
		if (allOptions.current && focusedIdx >= 0 && props.forcedFocusedIndex === undefined) {
			if (up) {
				setFocusedIdxIsMouseOver(false);
				if (true) {
					allOptions.current[valueInOrder[focusedIdx]?.value]?.current?.blur();
				}
				const newIdx = focusedIdx - 1;
				if (newIdx < valueInOrder.length && newIdx >= 0) {
					setFocusedIdx(newIdx);
					allOptions.current[valueInOrder[newIdx].value]?.current?.focus();
				}
				lastArrowScroll.current = new Date();
			}
		} else if (up && !focusedIdx) {
			setFocusedIdx(0);
		}
	}, [up]);

	React.useEffect(() => {
		if (allOptions.current && focusedIdx >= 0 && props.forcedFocusedIndex === undefined) {
			if (down) {
				setFocusedIdxIsMouseOver(false);
				if (true) {
					allOptions.current[valueInOrder[focusedIdx]?.value]?.current?.blur();
				}
				const newIdx = focusedIdx + 1;
				if (newIdx < valueInOrder.length && newIdx >= 0) {
					setFocusedIdx(newIdx);
					allOptions.current[valueInOrder[newIdx].value]?.current?.focus();
				}
				lastArrowScroll.current = new Date();
			}
		} else if (down && !focusedIdx) {
			setFocusedIdx(0);
		}
	}, [down]);

	// ensure highlight is always in view
	if (!focusedIdxIsMouseOver) {
		const menuBottom = menuRect.current?.scrollTop + menuRect.current?.offsetHeight;
		const highlightBottom = domRect.top + domRect.height;

		//const mousePos = useMousePosition();

		if (highlightBottom > menuBottom) {
			menuRect.current?.scroll({
				behavior: 'smooth',
				top: menuRect.current?.scrollTop + domRect.height
			});
		}

		const highlightTop = domRect.top;
		const menuTop = menuRect.current?.scrollTop;
		if (highlightTop < menuTop) {
			menuRect.current?.scroll({
				behavior: 'smooth',
				top: menuRect.current?.scrollTop - domRect.height
			});
		}
	}

	return (
		<Menu2Context.Provider
			value={{
				highlightedValues: props.highlightedValues ?? [],
				onSelect: c => {
					if (props.onSelect) {
						props.onSelect(c);
					}
				},
				focusedMenuValue: focusedIdx >= 0 ? valueInOrder[focusedIdx]?.value : '',
				removeChildOption: value => {
					delete allOptions.current[value];
				},
				addChildOption: (ref, val) => {
					allOptions.current = allOptions.current ?? {};
					allOptions.current[val] = ref;
				},
				setFocusedMenuOption: (value, s) => {
					const diff = moment.duration(moment(lastArrowScroll.current).diff(new Date())).milliseconds();
					if (lastArrowScroll.current === null || diff < -300) {
						const idx = valueInOrder.findIndex(v => v.value === value);
						setFocusedIdx(idx);
						if (idx < 0) {
							setFocusedIdx(0);
						}
						setFocusedIdxIsMouseOver(true);
					}
				}
			}}
		>
			<div
				onBlur={() => {
					setFocusedIdx(undefined);
				}}
				onScroll={() => {
					if (focusedIdxIsMouseOver) {
						//setToMenuRect();
						setFocusedIdx(-1);
					}
				}}
				style={props.style}
				ref={menuRect}
			>
				<Menu2Wrap
					tabIndex={0}
					onMouseOver={() => {
						menuRect.current?.focus();
					}}
					onBlur={() => setFocusedIdx(-1)}
					onMouseLeave={() => undefined}
				>
					<ItemCollection data={props.data}>{children}</ItemCollection>
				</Menu2Wrap>
			</div>

			{false && (
				<motion.div
					layout
					transition={{
						type: 'ease-in-out',
						duration: 0.2
					}}
					animate={{
						//backgroundColor: props.highlightedValues?.indexOf(selectedVal) >= 0 ? transparentize(0.5, theme.palette.swiftfox.orange['30-highlight']) : undefined,
						opacity: showHighlight ? 1 : 0
					}}
					style={{
						width: domRect?.width,
						height: domRect?.height,
						top: domRect?.top - menuRect?.current?.scrollTop,
						left: domRect?.left,
						position: 'fixed',
						pointerEvents: 'none'
					}}
				>
					<ItemBackground />
				</motion.div>
			)}
		</Menu2Context.Provider>
	);
};

export const Menu2Trigger = (props: {
	onVisibleChange?: (c: boolean) => void;
	visible?: boolean;
	placement?: Position;
	children: any;
	menu: IMenu2Props;
}) => {
	return (
		<Popover
			trigger="click"
			placement={props.placement ?? 'bottom-start'}
			onVisibleChange={props.onVisibleChange}
			visible={props.visible}
			overlay={<Menu2 {...props.menu} />}
		>
			<div style={{ cursor: 'pointer' }}>{props.children}</div>
		</Popover>
	);
};

/** Helper to enable a Menu2Option that embeds the Popover */
export const PopoverMenu2Option = ({
	overlay,
	...props
}: IMenu2OptionProps & { overlay: JSX.Element; placement?: Position; onVisibleChange?: (val: boolean) => void }) => {
	return (
		<Popover trigger="click" placement={props.placement ?? 'right'} overlay={overlay} onVisibleChange={props.onVisibleChange}>
			<div>
				<Menu2Option {...props} />
			</div>
		</Popover>
	);
};

/** Helper to enable a Menu2Option that embeds the ConfirmPopover */
export const ConfirmMenu2Option = ({ onConfirm, message, ...props }: IMenu2OptionProps & { onConfirm: () => void; message: string }) => {
	return (
		<ConfirmPopover trigger="click" placement="right" confirm={onConfirm} message={message}>
			<div>
				<Menu2Option {...props} />
			</div>
		</ConfirmPopover>
	);
};
